// Settings expansion (owner-approved 2026-11-02): audio mix, enemy health bars, brightness, FPS counter, controller vibration,
// touch layout (saved in the profile, save.ts v5 `settings`, so Export save carries them) and the per-device graphics
// overrides (render scale, particle density, grass density: localStorage like Quality / Frame rate, never exported).
// Pure: no DOM. main.ts applies them.

export type BarMode = 'off' | 'elite' | 'all';
export type RumbleLevel = 'off' | 'low' | 'high';
export type JoySize = 's' | 'm' | 'l';
export type Density = 'auto' | 'low' | 'medium' | 'high';

export interface SettingsPrefs {
  /** master volume, percent (0..100, steps of 10): everything, on top of the per-bus volumes */
  masterVol: number;
  /** sound-effects volume, percent (0..100, steps of 10): one-shot effects only (not music / ambience) */
  sfxVol: number;
  /** enemy health bars: off / elites & bosses / all (regular foes once damaged) */
  enemyBars: BarMode;
  /** final colour multiply, percent (70..130, steps of 5) */
  brightness: number;
  /** small corner FPS readout during runs */
  fpsCounter: boolean;
  /** controller vibration strength */
  rumble: RumbleLevel;
  /** touch joystick size */
  touchSize: JoySize;
  /** touch controls opacity, percent (40..100, steps of 10) */
  touchOpacity: number;
  /** left-handed: mirror the touch layout (Surge button on the left) */
  touchLeft: boolean;
}

/** owner 2026-11-03 'update the audio settings to be slider adjustments': 5% slider steps */
export const VOL_STEPS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100] as const;
export const BRIGHT_STEPS = [70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120, 125, 130] as const;
export const OPACITY_STEPS = [40, 50, 60, 70, 80, 90, 100] as const;
export const BAR_MODES: readonly BarMode[] = ['off', 'elite', 'all'];
export const RUMBLE_LEVELS: readonly RumbleLevel[] = ['off', 'low', 'high'];
export const JOY_SIZES: readonly JoySize[] = ['s', 'm', 'l'];
/** joystick travel / base size multiplier */
export const JOY_SCALE: Record<JoySize, number> = { s: 0.8, m: 1, l: 1.3 };

export function defaultSettings(): SettingsPrefs {
  return { masterVol: 100, sfxVol: 100, enemyBars: 'elite', brightness: 100, fpsCounter: false, rumble: 'low', touchSize: 'm', touchOpacity: 100, touchLeft: false };
}

/** the nearest allowed step (clamped to the range); non-numbers → fallback */
export function snap(v: unknown, steps: readonly number[], fallback: number): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  if (!Number.isFinite(n)) return fallback;
  const c = Math.max(steps[0], Math.min(steps[steps.length - 1], n));
  return steps.reduce((a, b) => (Math.abs(b - c) < Math.abs(a - c) ? b : a));
}
const pick = <T extends string>(v: unknown, all: readonly T[], d: T): T => (all.includes(v as T) ? (v as T) : d);

/** Upgrade any stored / imported shape (v4 and older saves have none: defaults). Never throws. */
export function migrateSettings(raw: unknown): SettingsPrefs {
  const p = defaultSettings();
  if (!raw || typeof raw !== 'object') return p;
  const r = raw as Partial<Record<keyof SettingsPrefs, unknown>>;
  p.masterVol = snap(r.masterVol, VOL_STEPS, p.masterVol);
  p.sfxVol = snap(r.sfxVol, VOL_STEPS, p.sfxVol);
  p.enemyBars = pick(r.enemyBars, BAR_MODES, p.enemyBars);
  p.brightness = snap(r.brightness, BRIGHT_STEPS, p.brightness);
  if (typeof r.fpsCounter === 'boolean') p.fpsCounter = r.fpsCounter;
  p.rumble = pick(r.rumble, RUMBLE_LEVELS, p.rumble);
  p.touchSize = pick(r.touchSize, JOY_SIZES, p.touchSize);
  p.touchOpacity = snap(r.touchOpacity, OPACITY_STEPS, p.touchOpacity);
  if (typeof r.touchLeft === 'boolean') p.touchLeft = r.touchLeft;
  return p;
}

// ---- per-device graphics overrides (localStorage arena.renderscale / arena.particles / arena.grass) ----
/** manual render scale, percent; 0 = Auto (the quality preset's own scale) */
export const RENDER_SCALES = [0, 50, 60, 70, 80, 90, 100] as const;
export const DENSITIES: readonly Density[] = ['auto', 'low', 'medium', 'high'];
/** particle pool size per density (the Low / Medium / High quality presets' own numbers) */
export const PARTICLE_CAPS: Record<Exclude<Density, 'auto'>, number> = { low: 900, medium: 2200, high: 3500 };

export interface GfxPrefs { renderScale: number; particles: Density; grass: Density }
export function defaultGfx(): GfxPrefs { return { renderScale: 0, particles: 'auto', grass: 'auto' }; }
/** from the stored strings (null = never set) */
export function parseGfx(scale: string | null, particles: string | null, grass: string | null): GfxPrefs {
  const s = scale === null || scale === '' || scale === 'auto' ? 0 : Number(scale);
  return {
    renderScale: !Number.isFinite(s) || s <= 0 ? 0 : snap(s, RENDER_SCALES.slice(1), 100),
    particles: pick(particles, DENSITIES, 'auto'),
    grass: pick(grass, DENSITIES, 'auto'),
  };
}
/** The render-scale cap (0..1) for a quality preset scale: a manual setting replaces the preset. Dynamic resolution then
 *  multiplies it by its own 0.5..1 factor, so it only ever lowers the resolution below this cap, never above it. */
export function renderScaleCap(presetScale: number, manualPct: number): number {
  return manualPct > 0 ? manualPct / 100 : presetScale;
}
/** the particle pool for this device: the override, or the preset's */
export function particleCap(presetCap: number, d: Density): number { return d === 'auto' ? presetCap : PARTICLE_CAPS[d]; }
/** the grass tier the maps build their 3D blade clumps for ('low' = painted ground only, as on Low quality) */
export function grassTier(quality: 'low' | 'medium' | 'high', d: Density): 'low' | 'medium' | 'high' { return d === 'auto' ? quality : d; }

// ---- Game speed (RUN SETUP row, owner 2026-11-02: chosen right before a run, fixed for the whole run) ----
export const GAME_SPEEDS = [70, 85, 100] as const;
/** the stored / selected value → 70 | 85 | 100 (anything else = full speed) */
export const gameSpeedOf = (v: unknown): number => snap(v, GAME_SPEEDS, 100);
/**
 * Real seconds → simulation seconds for the fixed-step accumulator. The sim always steps by its fixed DT (determinism is
 * untouched); a slower game speed only feeds it less real time, so fewer steps run per second. Render-only timers that
 * should stay in step with the world (particles, effect animations) are advanced by the same scaled time.
 */
export function simTime(realDt: number, speedPct: number): number {
  const k = snap(speedPct, GAME_SPEEDS, 100) / 100;
  return Math.max(0, realDt) * k;
}
